// MonacoComponent fully updated

import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as monaco from 'monaco-editor'
@Component({
  selector: 'hyperkit-code-editor',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="editor-container">
      <div #editorContainer class="monaco-container"></div>
    </div>
  `,
  styles: [
    `
    .editor-container {
      width: 100%;
      height: 300px;
      border: var(--Stroke-Size-Primary, 1px) solid var(--UI-surface-inputs-stroke-filled, #323333);
      border-radius: var(--Corner-Radius-Tertiary, 4px);
      background-color: var(--UI-panel-fill-01, rgba(17, 17, 17, 0.7));
      overflow: hidden;
    }
    .monaco-container {
      width: 100%;
      height: 100%;
    }
    `
  ]
})
export class MonacoComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;
  @Input() value = '';
  @Input() options: monaco.editor.IStandaloneEditorConstructionOptions = {};
  @Output() valueChange = new EventEmitter<string>(); // ✅ EMIT updated value

  private editor: monaco.editor.IStandaloneCodeEditor | null = null;
  private resizeObserver: ResizeObserver | null = null;

  ngOnInit(): void {
    const panelFill = getComputedStyle(document.documentElement)
      .getPropertyValue('--UI-panel-fill-01')
      .trim() || '#111111B3';

    const borderColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--UI-surface-inputs-stroke-filled')
      .trim() || '#323333';

    monaco.editor.defineTheme('hyperkit-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': panelFill,
        'editorWidget.border': borderColor,
        'editorGroup.border': borderColor,
        'focusBorder': borderColor
      }
    });
  }

  ngAfterViewInit(): void {
    const defaultOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
      value: this.value,
      language: 'json',
      theme: 'hyperkit-dark',
      fontSize: 14,
      automaticLayout: true,
      minimap: { enabled: false },
      wordWrap: 'on',
      lineNumbers: 'on',
      renderWhitespace: 'all',
      scrollBeyondLastLine: false
    };

    setTimeout(() => {
      this.editor = monaco.editor.create(this.editorContainer.nativeElement, {
        ...defaultOptions,
        ...this.options,
        value: this.value,
      });

      this.editor.onDidChangeModelContent(() => {
        const newValue = this.editor?.getValue() ?? '';
        this.valueChange.emit(newValue); // ✅ Emit back to parent
      });

      this.resizeObserver = new ResizeObserver(() => {
        this.editor?.layout();
      });

      this.resizeObserver.observe(this.editorContainer.nativeElement);
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.editor && !changes['value'].firstChange) {
      requestAnimationFrame(() => {
        const currentValue = this.editor?.getValue();
        if (this.value !== currentValue) {
          this.editor?.setValue(this.value);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.editor?.dispose();
    this.resizeObserver?.disconnect();
  }
}
